import { Button } from "@/components/ui/button";
import { ArrowRight, Sparkles, Wand2, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { useState, useEffect } from "react";

const TypewriterText = ({ text, delay = 0 }: { text: string; delay?: number }) => {
  const [displayedText, setDisplayedText] = useState("");
  const [currentIndex, setCurrentIndex] = useState(0);
  const [started, setStarted] = useState(false);

  useEffect(() => {
    const startTimeout = setTimeout(() => setStarted(true), delay);
    return () => clearTimeout(startTimeout);
  }, [delay]);

  useEffect(() => {
    if (!started) return;
    if (currentIndex < text.length) {
      const timeout = setTimeout(() => {
        setDisplayedText(prev => prev + text[currentIndex]);
        setCurrentIndex(prev => prev + 1);
      }, 50);
      return () => clearTimeout(timeout);
    }
  }, [currentIndex, text, started]);

  return (
    <span>
      {displayedText}
      {currentIndex < text.length && started && (
        <span className="animate-pulse text-primary">|</span>
      )}
    </span>
  );
};

const MiniDemo = () => {
  const [prompt, setPrompt] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [showResult, setShowResult] = useState(false);

  const examplePrompts = [
    "Un chat cosmique surfant sur une vague de galaxies",
    "Un château flottant dans les nuages au coucher du soleil",
    "Une forêt enchantée avec des lucioles magiques",
  ];

  const handleGenerate = () => {
    if (!prompt.trim()) {
      setPrompt(examplePrompts[Math.floor(Math.random() * examplePrompts.length)]);
    }
    setIsGenerating(true);
    setProgress(0);
    setShowResult(false);

    const interval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          setIsGenerating(false);
          setShowResult(true);
          return 100;
        }
        return prev + Math.random() * 15;
      });
    }, 200);
  };

  return (
    <div className="relative w-full max-w-md mx-auto">
      <div className="glass-card rounded-3xl p-5 border border-border/50">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-2.5 h-2.5 rounded-full bg-destructive/80" />
          <div className="w-2.5 h-2.5 rounded-full bg-highlight/80" />
          <div className="w-2.5 h-2.5 rounded-full bg-success/80" />
          <span className="ml-2 text-xs text-muted-foreground font-mono">floop.studio</span>
        </div>

        <div className="space-y-3">
          <div className="relative">
            <input
              type="text"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="Décrivez votre image..."
              className="w-full bg-secondary/50 border border-border/50 rounded-xl px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all"
              disabled={isGenerating}
            />
            <Wand2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          </div>

          <Button
            onClick={handleGenerate}
            disabled={isGenerating}
            className="w-full bg-gradient-floop hover:opacity-90 rounded-xl font-semibold text-white shadow-lg shadow-primary/30 transition-all duration-300"
            size="sm"
          >
            {isGenerating ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Génération...
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4 mr-2" />
                Générer avec Floop
              </>
            )}
          </Button>

          {isGenerating && (
            <div className="space-y-2">
              <div className="h-1.5 bg-secondary rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-floop transition-all duration-300 ease-out"
                  style={{ width: `${Math.min(progress, 100)}%` }}
                />
              </div>
              <p className="text-xs text-muted-foreground text-center">
                {progress < 50 ? "Génération..." : "Finalisation..."}
              </p>
            </div>
          )}

          {showResult && (
            <div className="relative rounded-xl overflow-hidden animate-scale-in">
              <div className="aspect-video bg-gradient-to-br from-primary/20 via-accent/20 to-violet/20 flex items-center justify-center">
                <div className="text-center p-4">
                  <Sparkles className="w-8 h-8 text-primary mx-auto mb-2 animate-pulse" />
                  <p className="text-xs text-muted-foreground">
                    Créez un compte pour voir vos créations !
                  </p>
                  <Link to="/auth">
                    <Button size="sm" variant="outline" className="mt-2 rounded-full text-xs">
                      Commencer
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="absolute -inset-4 bg-gradient-radial from-primary/15 via-transparent to-transparent blur-2xl -z-10" />
    </div>
  );
};

export const HeroSection = () => {
  const [mousePosition, setMousePosition] = useState({ x: 50, y: 50 });

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth) * 100,
        y: (e.clientY / window.innerHeight) * 100,
      });
    };

    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  return (
    <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden pt-20">
      {/* Simplified background */}
      <div className="absolute inset-0 bg-gradient-to-b from-background via-background/95 to-background" />
      
      {/* Single interactive gradient - reduced */}
      <div 
        className="absolute w-[500px] h-[500px] rounded-full opacity-20 blur-[100px] transition-all duration-1000 ease-out pointer-events-none"
        style={{
          background: 'radial-gradient(circle, hsl(var(--primary) / 0.4), transparent)',
          left: `${mousePosition.x}%`,
          top: `${mousePosition.y}%`,
          transform: 'translate(-50%, -50%)',
        }}
      />

      {/* Subtle orbs - reduced */}
      <div className="absolute top-1/3 left-1/4 w-64 h-64 bg-primary/10 rounded-full blur-[100px] animate-pulse" />
      <div className="absolute bottom-1/3 right-1/4 w-64 h-64 bg-accent/10 rounded-full blur-[100px] animate-pulse" style={{ animationDelay: '1s' }} />

      <div className="container mx-auto px-6 py-8 relative z-10">
        <div className="flex flex-col items-center text-center max-w-3xl mx-auto">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 glass-card border border-primary/30 rounded-full px-4 py-2 mb-6 animate-fade-in">
            <Sparkles className="w-3.5 h-3.5 text-primary animate-pulse" />
            <span className="text-xs text-foreground font-medium">
              IA de génération d'images
            </span>
          </div>
          
          {/* Main Title - Centered and reduced */}
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 leading-tight font-display">
            <span className="block text-foreground opacity-0 animate-slide-up" style={{ animationDelay: "0.2s", animationFillMode: "forwards" }}>
              Créez des visuels époustouflants
            </span>
            <span className="block mt-1">
              <span className="gradient-text-animated">
                <TypewriterText text="en 10 secondes" delay={600} />
              </span>
            </span>
          </h1>
          
          <p className="text-base md:text-lg text-muted-foreground max-w-lg mb-8 leading-relaxed opacity-0 animate-fade-up" style={{ animationDelay: "1.2s", animationFillMode: "forwards" }}>
            Décrivez votre image, Floop la génère. Qualité HD et 4K, styles illimités, résultats professionnels instantanés.
          </p>
          
          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center gap-3 mb-8 opacity-0 animate-fade-up" style={{ animationDelay: "1.5s", animationFillMode: "forwards" }}>
            <Link to="/create">
              <Button 
                size="lg" 
                className="bg-gradient-floop hover:opacity-90 rounded-full px-8 py-5 text-base font-semibold group shadow-xl shadow-primary/30 transition-all duration-300 hover:scale-105 text-white"
              >
                Essayer gratuitement
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
            <Link to="/pricing">
              <Button 
                size="lg" 
                variant="outline"
                className="rounded-full px-6 py-5 text-sm font-medium border-border/50 hover:bg-secondary/50 transition-all duration-300"
              >
                Voir les tarifs
              </Button>
            </Link>
          </div>

          {/* Trust badges - compact */}
          <div className="flex flex-wrap items-center justify-center gap-4 opacity-0 animate-fade-up" style={{ animationDelay: "1.8s", animationFillMode: "forwards" }}>
            {["Sans carte bancaire", "10 crédits offerts", "Résultats en 10s"].map((text, i) => (
              <div key={i} className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <div className="w-4 h-4 rounded-full bg-success/20 flex items-center justify-center">
                  <svg className="w-2.5 h-2.5 text-success" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
                {text}
              </div>
            ))}
          </div>

          {/* Mini Demo - below text */}
          <div className="mt-10 w-full opacity-0 animate-scale-in" style={{ animationDelay: "0.5s", animationFillMode: "forwards" }}>
            <MiniDemo />
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 opacity-0 animate-fade-in" style={{ animationDelay: "2.5s", animationFillMode: "forwards" }}>
        <div className="flex flex-col items-center gap-1.5 text-muted-foreground">
          <span className="text-[10px] uppercase tracking-wider">Découvrir</span>
          <div className="w-5 h-8 border-2 border-muted-foreground/30 rounded-full flex justify-center pt-1.5">
            <div className="w-1 h-2 bg-primary rounded-full animate-scroll-indicator" />
          </div>
        </div>
      </div>
    </section>
  );
};